// Editor chrome: toolbar buttons, the shape split-menu, pane resize
// handles and the document tab bar.
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  SHAPE_META,
  shortcutLabel,
  type EditorCommand,
  type ToolGroup,
} from "../lib/commands.ts";
import type { ShapeKind } from "../lib/protocol.ts";
import type { DocTab } from "../lib/state.ts";
import { isShapeTool, type Tool } from "../lib/ui-types.ts";

// ─────────────────────────── toolbar bits ───────────────────────────

/** One registry command as a toolbar button. The point of routing
 *  buttons through here is that the toolbar, the command palette and the
 *  keyboard all read the SAME table — adding a command gives you all
 *  three, instead of a button here and a near-duplicate entry there. */
export function CommandButton({ command }: { command: EditorCommand }) {
  const shortcut = shortcutLabel(command.shortcut);
  return (
    <button
      type="button"
      title={shortcut ? `${command.label} (${shortcut})` : command.label}
      aria-pressed={command.active}
      disabled={command.disabled}
      onClick={command.run}
      className={`inline-flex h-8 w-8 items-center justify-center rounded-md disabled:opacity-40 ${
        command.active
          ? "bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]"
          : "hover:bg-[color:var(--color-bg-subtle)]"
      }`}
    >
      <i className={command.icon} aria-hidden />
    </button>
  );
}

export function ToolButton({
  icon,
  label,
  shortcut,
  active,
  onClick,
}: {
  icon: string;
  label: string;
  shortcut: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      title={`${label} (${shortcut})`}
      aria-pressed={active}
      onClick={onClick}
      className={`inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-sm ${
        active
          ? "bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]"
          : "text-[color:var(--color-fg)] hover:bg-[color:var(--color-bg-subtle)]"
      }`}
    >
      <i className={icon} aria-hidden />
      <span className="hidden xl:inline">{label}</span>
    </button>
  );
}

/** Drag-to-resize handle between the page view and a side pane. The
 *  width lives in the parent so it survives panel switches; `side`
 *  says which way the pointer moves to widen. */
export function PaneResizer({
  side,
  width,
  min,
  max,
  onResize,
}: {
  side: "left" | "right";
  width: number;
  min: number;
  max: number;
  onResize: (w: number) => void;
}) {
  const start = useRef<{ x: number; w: number } | null>(null);
  return (
    <div
      className="pe-resizer"
      role="separator"
      aria-orientation="vertical"
      title="Drag to resize"
      onPointerDown={(e) => {
        e.preventDefault();
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
        start.current = { x: e.clientX, w: width };
      }}
      onPointerMove={(e) => {
        const st = start.current;
        if (!st) return;
        const delta = side === "left" ? e.clientX - st.x : st.x - e.clientX;
        onResize(Math.max(min, Math.min(max, st.w + delta)));
      }}
      onPointerUp={() => {
        start.current = null;
      }}
      onDoubleClick={() => onResize(side === "left" ? 112 : 288)}
    />
  );
}

/** One verb group as a split button. Inactive click arms the group's
 *  last-used member; active click opens the picker for the rest. Holding
 *  the button (or double-clicking) LOCKS the tool, so it stays armed
 *  after each use instead of springing back to the pointer.
 *
 *  This replaces a row of twenty-one flat tool buttons. Nothing was
 *  removed — every tool is a member of exactly one group, and the
 *  button remembers your last pick, so reaching for the one you just
 *  used is still a single click. */
export function ToolMenu({
  group,
  tool,
  lastPick,
  locked,
  onPick,
  onToggleLock,
}: {
  group: ToolGroup;
  tool: Tool;
  /** The member shown on the button — the last one used in this group. */
  lastPick: Tool;
  /** True when the armed tool will stay armed after it makes something. */
  locked: boolean;
  onPick: (t: Tool) => void;
  onToggleLock: () => void;
}) {
  const [open, setOpen] = useState(false);
  const held = useRef<number | null>(null);
  const active = group.members.some((m) => m.tool === tool);
  const current = group.members.find((m) => m.tool === (active ? tool : lastPick)) ?? group.members[0]!;
  const single = group.members.length === 1;

  // The hold is detected on pointer events, but the ACTION stays on
  // click — otherwise the button stops working from the keyboard, which
  // is how a fair number of people reach the toolbar at all.
  const fired = useRef(false);
  const startHold = () => {
    fired.current = false;
    held.current = window.setTimeout(() => {
      held.current = null;
      fired.current = true;
      onPick(current.tool);
      onToggleLock();
    }, 450);
  };
  const endHold = () => {
    if (held.current !== null) window.clearTimeout(held.current);
    held.current = null;
  };

  return (
    <div className="relative">
      <button
        type="button"
        title={
          `${current.label}${current.key ? ` (${current.key.toUpperCase()})` : ""}` +
          (single ? "" : " — click again to switch tool") +
          " · hold to keep it armed"
        }
        aria-pressed={active}
        aria-haspopup={single ? undefined : "menu"}
        onPointerDown={startHold}
        onPointerUp={endHold}
        onPointerLeave={endHold}
        onClick={() => {
          if (fired.current) {
            // The hold already armed and locked it — don't also toggle.
            fired.current = false;
            return;
          }
          if (active && !single) setOpen((o) => !o);
          else onPick(current.tool);
        }}
        onDoubleClick={() => {
          onPick(current.tool);
          onToggleLock();
        }}
        className={`inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-sm ${
          active
            ? "bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]"
            : "text-[color:var(--color-fg)] hover:bg-[color:var(--color-bg-subtle)]"
        }`}
      >
        <i className={current.icon} aria-hidden />
        <span className="hidden xl:inline">{current.label}</span>
        {active && locked && <i className="ri-pushpin-fill text-[10px]" aria-hidden />}
        {!single && <i className="ri-arrow-down-s-line -ml-1 opacity-70" aria-hidden />}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} aria-hidden />
          <div
            role="menu"
            className="absolute left-0 top-full z-40 mt-1 w-48 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] p-1 shadow-lg"
          >
            {group.members.map((m) => (
              <button
                key={m.tool}
                type="button"
                role="menuitem"
                title={m.hint}
                onClick={() => {
                  onPick(m.tool);
                  setOpen(false);
                }}
                className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-[color:var(--color-bg-subtle)] ${
                  m.tool === current.tool ? "text-[color:var(--color-accent)]" : ""
                }`}
              >
                <i className={m.icon} aria-hidden />
                {m.label}
                {m.key && (
                  <span className="ml-auto text-xs text-[color:var(--color-fg-muted)]">
                    {m.key.toUpperCase()}
                  </span>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** Split-ish shape button: inactive click activates the last-used
 *  shape; active click opens the picker for the other shapes. */
export function ShapeMenu({
  tool,
  shapeKind,
  onPick,
}: {
  tool: Tool;
  shapeKind: ShapeKind;
  onPick: (s: ShapeKind) => void;
}) {
  const [open, setOpen] = useState(false);
  const active = isShapeTool(tool);
  const current = active ? tool : shapeKind;
  const meta = SHAPE_META[current];
  return (
    <div className="relative">
      <button
        type="button"
        title={`${meta.label} (${meta.shortcut}) — click again for more shapes`}
        aria-pressed={active}
        aria-haspopup="menu"
        onClick={() => {
          if (active) setOpen((o) => !o);
          else onPick(current);
        }}
        className={`inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-sm ${
          active
            ? "bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]"
            : "text-[color:var(--color-fg)] hover:bg-[color:var(--color-bg-subtle)]"
        }`}
      >
        <i className={meta.icon} aria-hidden />
        <span className="hidden xl:inline">{meta.label}</span>
        <i className="ri-arrow-down-s-line -ml-1 opacity-70" aria-hidden />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} aria-hidden />
          <div
            role="menu"
            className="absolute left-0 top-full z-40 mt-1 w-40 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] p-1 shadow-lg"
          >
            {(Object.keys(SHAPE_META) as ShapeKind[]).map((k) => (
              <button
                key={k}
                type="button"
                role="menuitem"
                onClick={() => {
                  onPick(k);
                  setOpen(false);
                }}
                className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-[color:var(--color-bg-subtle)] ${
                  current === k ? "text-[color:var(--color-accent)]" : ""
                }`}
              >
                <i className={SHAPE_META[k].icon} aria-hidden />
                {SHAPE_META[k].label}
                <span className="ml-auto text-xs text-[color:var(--color-fg-muted)]">
                  {SHAPE_META[k].shortcut}
                </span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** Simplified version of the image editor's tab bar: select, close,
 *  double-click rename, and a "+" for a new blank document. */
export function TabBar({
  tabs,
  activeId,
  dirtyIds,
  onSelect,
  onClose,
  onRename,
  onNew,
}: {
  tabs: DocTab[];
  activeId: string | null;
  dirtyIds: Set<string>;
  onSelect: (id: string) => void;
  onClose: (id: string) => void;
  onRename: (id: string, name: string) => void;
  /** Omitted = no "+" button (the host decides what is open). */
  onNew?: () => void;
}) {
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");

  function commitRename(): void {
    if (!renamingId) return;
    const trimmed = renameValue.trim();
    if (trimmed) onRename(renamingId, trimmed.endsWith(".pdf") ? trimmed : `${trimmed}.pdf`);
    setRenamingId(null);
  }

  return (
    <div className="flex items-stretch gap-0.5 overflow-x-auto border-b border-[color:var(--color-border)] bg-[color:var(--color-bg-subtle)] px-1 pt-1 text-xs">
      {tabs.map((tab) => {
        const active = tab.id === activeId;
        const isRenaming = renamingId === tab.id;
        return (
          <div
            key={tab.id}
            onClick={() => {
              if (!isRenaming) onSelect(tab.id);
            }}
            onDoubleClick={() => {
              setRenamingId(tab.id);
              setRenameValue(tab.name);
            }}
            className={
              "group flex max-w-[220px] shrink-0 cursor-pointer select-none items-center gap-1.5 rounded-t-md border border-b-0 px-3 py-1.5 " +
              (active
                ? "border-[color:var(--color-border)] bg-[color:var(--color-bg)] text-[color:var(--color-fg)]"
                : "border-transparent text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg)]")
            }
            title="Double-click to rename"
          >
            {isRenaming ? (
              <input
                autoFocus
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    commitRename();
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    setRenamingId(null);
                  }
                  e.stopPropagation();
                }}
                onBlur={commitRename}
                onFocus={(e) => e.target.select()}
                className="w-32 min-w-0 rounded border border-[color:var(--color-border-strong)] bg-[color:var(--color-bg)] px-1 py-0 text-[color:var(--color-fg)]"
              />
            ) : (
              <span className="min-w-0 truncate">
                {dirtyIds.has(tab.id) ? "• " : ""}
                {tab.name}
              </span>
            )}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onClose(tab.id);
              }}
              title="Close tab"
              aria-label={`Close ${tab.name}`}
              className="ml-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-border)] hover:text-[color:var(--color-fg)]"
            >
              ×
            </button>
          </div>
        );
      })}
      {onNew && (
        <button
          type="button"
          onClick={onNew}
          title="New blank document"
          aria-label="New blank document"
          className="ml-1 flex shrink-0 items-center rounded px-2 py-1.5 text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg)] hover:text-[color:var(--color-fg)]"
        >
          +
        </button>
      )}
    </div>
  );
}

// ─────────────────────────── context menu ───────────────────────────

export type ContextMenuItem =
  | {
      label: string;
      icon?: string;
      /** Display-only shortcut hint (right-aligned). */
      shortcut?: string;
      danger?: boolean;
      disabled?: boolean;
      run: () => void;
    }
  | "separator";

/** Right-click menu: fixed-positioned, viewport-clamped, closes on
 *  click-away or Escape (capture phase, so the editor's global Escape
 *  doesn't also clear the selection underneath). */
export function EditorContextMenu({
  x,
  y,
  items,
  onClose,
}: {
  x: number;
  y: number;
  items: ContextMenuItem[];
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: x, top: y });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({
      left: Math.max(8, Math.min(x, window.innerWidth - r.width - 8)),
      top: Math.max(8, Math.min(y, window.innerHeight - r.height - 8)),
    });
  }, [x, y]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopImmediatePropagation();
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  return (
    <>
      <div
        className="fixed inset-0 z-40"
        onPointerDown={onClose}
        onContextMenu={(e) => {
          e.preventDefault();
          onClose();
        }}
        aria-hidden
      />
      <div
        ref={ref}
        role="menu"
        className="fixed z-50 w-56 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] p-1 shadow-lg"
        style={{ left: pos.left, top: pos.top }}
      >
        {items.map((item, i) =>
          item === "separator" ? (
            <div key={i} className="mx-2 my-1 h-px bg-[color:var(--color-border)]" aria-hidden />
          ) : (
            <button
              key={i}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              onClick={() => {
                onClose();
                item.run();
              }}
              className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40 ${
                item.danger ? "text-[color:var(--color-danger,#e03131)]" : ""
              }`}
            >
              {item.icon && <i className={item.icon} aria-hidden />}
              <span className="min-w-0 flex-1 truncate">{item.label}</span>
              {item.shortcut && (
                <span className="text-xs text-[color:var(--color-fg-muted)]">{item.shortcut}</span>
              )}
            </button>
          )
        )}
      </div>
    </>
  );
}
